import type { OralExamTimeSlotStateResponse } from '@/common/api/models/oralExamTimeSlotStateResponse';
import {
  deriveSupervisorStatusForViewer,
  viewerActiveDropInBookingId,
} from '@/common/oralExam/supervisorVisits';
import { unwrapOralExamCohortState } from './unwrapOralExamCohortState';

export function unwrapOralExamTimeSlotState(data: unknown): OralExamTimeSlotStateResponse | null {
  if (!data || typeof data !== 'object') return null;
  const layer = data as { data?: { data?: OralExamTimeSlotStateResponse } };
  return layer.data?.data ?? null;
}

/**
 * Merge a drop-in POST snapshot into the cached cohort aggregate so the wall
 * updates before the next poll (supervisors, etag, viewer supervisorStatus).
 */
export function applyDropInToCohortCache(
  cached: unknown,
  mutationPayload: unknown,
  viewerLecturerId: string,
): unknown {
  const slotState = unwrapOralExamTimeSlotState(mutationPayload);
  if (!slotState) return cached;

  const cohort = unwrapOralExamCohortState(cached);
  if (!cohort?.bookingRuns) return cached;

  const supervisors = slotState.supervisors ?? cohort.supervisors;
  const slotOwnerId = slotState.slotOwnerId ?? cohort.slotOwnerId;
  const bookingRuns = cohort.bookingRuns.map((run) => ({
    ...run,
    supervisorStatus: deriveSupervisorStatusForViewer(
      supervisors,
      run.bookingId,
      viewerLecturerId,
    ),
  }));

  const envelope = cached as { data?: { data?: Record<string, unknown> } };
  return {
    ...envelope,
    data: {
      ...envelope.data,
      data: {
        ...cohort,
        sessionPhase: slotState.sessionPhase ?? cohort.sessionPhase,
        inExamEnteredAt: slotState.inExamEnteredAt ?? cohort.inExamEnteredAt,
        admittedBookingIds: slotState.admittedBookingIds ?? cohort.admittedBookingIds,
        brieferId: slotState.brieferId ?? cohort.brieferId,
        slotOwnerId,
        supervisors,
        stageGate: slotState.stageGate ?? cohort.stageGate,
        etag: slotState.etag ?? cohort.etag,
        activeDropInBookingId: viewerActiveDropInBookingId(supervisors, viewerLecturerId),
        bookingRuns,
      },
    },
  };
}
